/*
 * Run an asynchronous batch of document edits so one Cmd+Z reverses the whole
 * batch.
 *
 * The collaborative editor's undo stack is a Yjs UndoManager, and TWO separate
 * things decide where its undo steps begin:
 *
 * 1. `captureTimeout` (500ms by default — y-prosemirror constructs the manager
 *    without an override). "Accept all" applies one suggestion per FHIR round
 *    trip, so this alone would let network latency decide whether N accepts cost
 *    the clinician one Cmd+Z or N of them.
 * 2. y-prosemirror's sync plugin calls `undoManager.stopCapturing()` on every
 *    local transaction tagged `addToHistory: false` (sync-plugin.js:213-224),
 *    which zeroes `lastChange` and so forces the NEXT change to open a fresh undo
 *    step. The accept path fires exactly such transactions between accepts — the
 *    stamp's completion patch and the trailing-paragraph maintenance are both
 *    deliberately history-suppressed — so a batch was being chopped into steps at
 *    points that have nothing to do with what the clinician did.
 *
 * Pinning the timeout alone leaves (2) in charge, which is what a live probe of
 * the undo stack showed: three accepts, three separate steps. So the batch also
 * shadows `stopCapturing` for its duration. A history-suppressed transaction is
 * not captured by the manager either way; suppressing the call only stops it from
 * breaking the group around it.
 *
 * The `stopCapturing` calls on either side are the other half: without the first,
 * the batch absorbs the clinician's preceding keystrokes; without the second,
 * their next keystroke lands inside the batch's undo step. An edit made DURING
 * the batch still joins it — the run is short and the editor is mid-action, and
 * splitting a partially-applied batch across undo steps would be worse.
 *
 * A non-collaborative editor registers no yUndoPlugin and so has no UndoManager;
 * there the batch simply runs.
 */
import type { Editor } from '@tiptap/react'
import { yUndoPluginKey } from 'y-prosemirror'

const noop = (): void => {}

export interface BulkEditBatch {
  /*
   * An undo has landed since this batch started. Grouping makes the batch one
   * undo step, so a Cmd+Z taken while it is still applying takes back everything
   * applied so far — and the clinician who pressed it is asking the batch to
   * stop. `run` must check this between edits: measured live, a batch that kept
   * going re-applied content into the note the undo had just removed, and left a
   * record durably accepted whose text was nowhere in the note and whose card was
   * gone. Set by ANY undo during the batch, not only one that pops this batch's
   * own stack item — an undo of something else still moves the document the
   * remaining edits resolve against.
   */
  readonly undone: boolean
}

export async function withSingleUndoStep(
  editor: Editor,
  run: (batch: BulkEditBatch) => Promise<void>
): Promise<void> {
  const undoManager = yUndoPluginKey.getState(editor.state)?.undoManager
  if (!undoManager) {
    // No UndoManager, so no undo can land: nothing for `run` to stop for.
    await run({ undone: false })
    return
  }
  const batch = { undone: false }
  const onPopped = (event: { type: 'undo' | 'redo' }): void => {
    if (event.type === 'undo') {
      batch.undone = true
    }
  }
  undoManager.on('stack-item-popped', onPopped)
  const previousTimeout = undoManager.captureTimeout
  undoManager.stopCapturing()
  undoManager.captureTimeout = Number.POSITIVE_INFINITY
  // Shadows the prototype method; deleting the own property below restores it.
  undoManager.stopCapturing = noop
  try {
    await run(batch)
  } finally {
    undoManager.off('stack-item-popped', onPopped)
    Reflect.deleteProperty(undoManager, 'stopCapturing')
    undoManager.captureTimeout = previousTimeout
    undoManager.stopCapturing()
  }
}
